/*
 * PageWright — shared My Library token storage (2026-08-13).
 *
 * pw-library.js, pw-favorites.js and pw-in-cart.js each grew their own copy of "read the
 * pw_library_token" -- and one already drifted: pw-library.js was fixed 2026-08-02 to fall back
 * from sessionStorage to localStorage (Paul: "if you are in your library then navigate away and
 * try to come back you are forced to login again"), but pw-favorites.js and pw-in-cart.js still
 * read sessionStorage ONLY, which is destroyed on browser close. Both load site-wide
 * (pw-favorites.js from sections/pw-site-header.liquid, pw-in-cart.js from layout/theme.liquid),
 * so a browser restart de-identified the customer everywhere except the one page that already
 * had the fix.
 *
 * One shared helper so a fourth copy can't drift the same way again. Loaded before every
 * consumer's own script tag (see layout/theme.liquid) -- deferred scripts execute in document
 * order, so window.PWAuth exists before any consumer's deferred code runs, the same guarantee
 * window.PW_BACKEND_URL already relies on.
 */
(function () {
  'use strict';

  var TOKEN_KEY = 'pw_library_token';
  /* pw-in-cart.js's in-cart/ownership cache (sessionStorage) and pw-favorites.js's
   * last-merged-token marker (localStorage, a full copy of the JWT written on every page load
   * with a token present) -- both cleared alongside the token itself by clearToken() so sign-out
   * actually sticks. pw_favorites is NOT cleared by clearToken() itself (guest favorites are
   * deliberately device-local, and a prior incident already destroyed a customer's unsynced
   * favorites once via an unrelated "clear all site data" support instruction) -- it's cleared
   * only by signOutCleanup() below, on an actual sign-out/account-switch transition, where it's
   * no longer guest data (see that function's own comment). */
  var OWNED_CACHE_KEY = 'pw_owned_handles';
  var LAST_MERGED_TOKEN_KEY = 'pw_favorites_last_merged_token';

  function getToken() {
    try {
      return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || undefined;
    } catch (e) {
      return undefined;
    }
  }

  /* Writes BOTH stores -- verbatim semantics from pw-library.js's 2026-08-02 fix. Rejects
   * anything that isn't a real, non-empty string: a coerced non-string (object/number/array)
   * would otherwise get stringified into storage via setItem and poison the token silently. */
  function storeToken(token) {
    if (typeof token !== 'string' || !token) return;
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      /* storage full/blocked — fall through to sessionStorage below */
    }
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      /* storage full/blocked — token just won't persist */
    }
  }

  /*
   * Clear BOTH stores for the token itself. This is the dangerous half of a second store:
   * leaving one store untouched lets a revoked or expired token resurrect from it on the very
   * next load -- a sign-out that never sticks. One helper, so no call site can drift.
   *
   * Also clears the two other places a customer's identity persists once this token exists --
   * pw_owned_handles (sessionStorage) and pw_favorites_last_merged_token (localStorage, a
   * durable copy of the JWT). Without this, a sign-out leaves a stale ownership cache and a
   * live second credential sitting in storage after the "real" token is gone. Every call site
   * (a plain 401/expiry as well as a real sign-out via signOutCleanup below) gets this for free.
   */
  function clearToken() {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
    try {
      sessionStorage.removeItem(OWNED_CACHE_KEY);
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
    try {
      localStorage.removeItem(LAST_MERGED_TOKEN_KEY);
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
  }

  /*
   * Decode a JWT's payload segment ONLY -- no signature verification. That's correct here: the
   * server already verified this token (it's either what /api/library-session just minted, or
   * what's already in storage from a prior verified response); the client only needs `exp`/
   * `customerId`/`email` to compare two ALREADY-TRUSTED tokens. Never throws -- a malformed or
   * missing token returns undefined, handled as "can't verify, use the safe default" by every
   * caller.
   *
   * tasks.db #816 (2026-09-01, PLAN v3 §2.3, Opus SF-8): this used to be a raw `atob` + JSON.parse
   * decode, which mojibakes any non-ASCII byte in the payload (each byte becomes its own UTF-16
   * code unit instead of being reassembled as a UTF-8 sequence). That was harmless while the only
   * claims read here were numeric (`exp`/`customerId`), but adding the `email` claim on this same
   * path would turn every non-ASCII email (a real, live class of Shopify customer email) into a
   * false identity mismatch the moment #816's cross-check compares it. Fixed by adopting the
   * SAME percent-decoding UTF-8-safe technique assets/pw-identity.js's decodeEmail already used --
   * this is now the ONE shared decoder; pw-identity.js delegates to this function when PWAuth has
   * loaded (see its own comment) rather than keeping a second, divergent implementation.
   */
  function tokenClaims(token) {
    if (!token) return undefined;
    try {
      var seg = token.split('.')[1];
      if (!seg) return undefined;
      var b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      var json = decodeURIComponent(
        atob(b64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      var payload = JSON.parse(json);
      return {
        exp: typeof payload.exp === 'number' ? payload.exp : undefined,
        customerId: typeof payload.customerId === 'number' ? payload.customerId : undefined,
        email: typeof payload.email === 'string' && payload.email ? payload.email : undefined,
      };
    } catch (e) {
      return undefined;
    }
  }

  function tokenExp(token) {
    var claims = tokenClaims(token);
    return claims ? claims.exp : undefined;
  }

  /*
   * Should a newly-bridged/renewed token REPLACE what's already stored? Pulled out of
   * trySessionBridge (pw-library.js) so it's one pure, directly-testable decision instead of
   * logic buried inside a DOM-heavy function (2026-08-13/14 review finding: the original inline
   * version had zero real test coverage and four different single-line mutations of it all
   * shipped a green suite).
   *
   * Rules, in order:
   *  - nothing currently stored (or unreadable) -> nothing to protect, always replace.
   *  - the NEW token doesn't decode -> fail closed, keep the good one we already have (a
   *    malformed token from the server would otherwise silently evict a working credential).
   *  - the two tokens are for DIFFERENT customers -> identity always wins, always replace
   *    (comparing lifetimes across two different people's credentials is meaningless).
   *  - same customer -> replace unless the new token is a genuine downgrade (shorter-lived).
   */
  function shouldReplaceToken(currentToken, newToken) {
    var cur = tokenClaims(currentToken);
    if (!cur) return true;
    var next = tokenClaims(newToken);
    if (!next) return false;
    if (
      cur.customerId !== undefined &&
      next.customerId !== undefined &&
      cur.customerId !== next.customerId
    ) {
      return true;
    }
    if (cur.exp !== undefined && next.exp !== undefined && next.exp < cur.exp) {
      return false;
    }
    return true;
  }

  /* Same beacon pipeline pw-favorites.js's analytics already uses (dev-rules §13) -- best-effort,
   * no PII, must never throw or block sign-out itself. */
  function postSignOutEvent(reason) {
    try {
      var backendUrl = window.PW_BACKEND_URL;
      if (!backendUrl) return;
      var body = JSON.stringify({ eventVersion: 1, event: 'library.session.cleared', reason: reason });
      var url = backendUrl + '/api/collect';
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else if (typeof fetch !== 'undefined') {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break sign-out */
    }
  }

  /*
   * Real sign-out (2026-08-13/14 login forensics, defect: "explicitly signing out of Shopify does
   * not clear the library token... on a shared computer the next person can see the previous
   * customer's library for up to 14 days"). Paired requirement for raising the Shopify-login
   * bridge's TTL off 30 minutes -- a longer-lived credential on a shared computer is only safe
   * with a working sign-out.
   *
   * theme.liquid stamps window.PW_CUSTOMER_ID = {{ customer.id | json }} on every page view (an
   * INLINE script, not deferred -- inline scripts run synchronously at parse time, before any
   * deferred script including this file, so the stamp is always current before syncIdentity runs
   * -- the same guarantee window.PW_BACKEND_URL already relies on). We compare it against the
   * last value we saw, stored in localStorage.
   *
   * CRITICAL invariant, must never regress: an emailed-link visitor is never Shopify-logged-in --
   * window.PW_CUSTOMER_ID is empty on every page view for them. No stamp AND no current customer
   * must be a complete no-op, not even a stamp-key write. That's the exact case the sliding-token
   * renewal (handleLibrary) exists to keep alive; clearing here would silently undo it. Cleanup
   * fires ONLY on a genuine TRANSITION: a stamp was recorded, and the current customer is now
   * empty (signed out) or different (switched accounts).
   */
  var CUSTOMER_STAMP_KEY = 'pw_customer_id_stamp';

  /*
   * 2026-08-13/14 review finding (H1): pw_favorites is NOT guest-only once a customer has ever
   * logged in -- pw-favorites.js's mergeOnLogin REPLACES it wholesale with that customer's
   * server-side list. Leaving it untouched on sign-out means the next person on a shared
   * computer sees the previous customer's favorites; worse, clearToken() clearing
   * pw_favorites_last_merged_token without ALSO clearing pw_favorites here would mean the NEXT
   * customer's login re-merges A's still-resident list into B's account server-side the moment B
   * gets any token -- a real cross-customer data leak, not theoretical. So pw_favorites IS
   * cleared here now, on every real sign-out/switch transition. This does not touch the separate,
   * unrelated "clear all site data" support-flow incident (that was about destroying a GUEST's
   * never-logged-in favorites; this only ever fires on a stamped transition, which by definition
   * means an account was involved).
   */
  function signOutCleanup(reason) {
    clearToken(); // also clears pw_owned_handles + pw_favorites_last_merged_token
    try {
      localStorage.removeItem('pw_favorites');
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
    postSignOutEvent(reason);
  }

  /*
   * tasks.db #816 (2026-09-01, PLAN v3) -- identity cross-check.
   *
   * A cryptographically valid pw:library token can belong to a DIFFERENT real customer than
   * whoever Shopify's own session says is really logged in right now (Shop Pay silently
   * re-authenticates as its own saved identity; a shared computer; a hostile emailed link). No
   * existing code path ever compared the token's OWNER against the real session -- this does.
   *
   * `identityCheck(claims, sessionCustomerId, sessionEmail)` returns one of the five decision
   * values ALSO used verbatim as the telemetry `decision` prop (no separate vocabulary/mapping
   * layer to keep in sync):
   *   - 'match'                        -- same id (or no session identity to compare against).
   *   - 'same_person'                  -- different id, same email (Shop Pay dual-record case).
   *   - 'mismatch'                     -- different id, both emails known and different.
   *   - 'indeterminate_undecodable'    -- token doesn't decode, or is missing customerId.
   *   - 'indeterminate_no_session_email' -- ids differ but EITHER email is unknown/empty (real
   *     Shopify state for SMS/phone-only accounts -- Audit-1 MF-2). NEVER clears.
   *
   * Round-2 Opus MF-2 (the reason this is spec, not "just compare them"): theme.liquid stamps
   * `window.PW_CUSTOMER_ID = {{ customer.id | json }}` as a NUMBER; `pw-library.js`'s
   * `root.dataset.customerId` reads it back as a STRING; `tokenClaims().customerId` is a NUMBER.
   * A naive `===` mass-fires `mismatch` for every ordinary logged-in customer depending on which
   * side of the comparison happened to be typed. Fixed by normalizing -- but round-2 ALSO found
   * that normalizing via `String(x).trim()` before checking for "unknown" is itself a bug:
   * `{{ customer.id | json }}` renders the literal `null` for a logged-out visitor, and
   * `String(null)` is the non-empty string `"null"` (same for `undefined`). Checking "is this
   * unknown?" AFTER coercion would read a logged-out visitor's id as present (breaking the
   * sitewide no-op invariant) and would make two unknown emails compare EQUAL to each other
   * (blessing a foreign token as `same_person`). So the unknown check runs FIRST, on the RAW
   * value, before any `String()` call -- only a value that survives it gets normalized.
   */
  function isUnknownValue(x) {
    return x === null || x === undefined || x === '' || (typeof x === 'string' && x.trim() === '');
  }

  function identityCheck(claims, sessionCustomerId, sessionEmail) {
    try {
      // No session identity to compare against (emailed-link visitor, or a caller that hasn't
      // resolved one) -- nothing to cross-check, so there is nothing to disagree with.
      if (isUnknownValue(sessionCustomerId)) return 'match';
      if (!claims || isUnknownValue(claims.customerId)) return 'indeterminate_undecodable';

      var sessionId = String(sessionCustomerId).trim();
      var tokenId = String(claims.customerId).trim();
      if (sessionId === tokenId) return 'match';

      // Ids differ. Only emails can tell same-person-dual-record apart from a real mismatch --
      // and only once BOTH are known (Audit-1 SF-4: token emails sourced from order/webhook data
      // are not guaranteed pre-trimmed; a blank/null session email is real Shopify state for
      // SMS/phone-only accounts, Audit-1 MF-2).
      if (isUnknownValue(sessionEmail) || isUnknownValue(claims.email)) {
        return 'indeterminate_no_session_email';
      }

      // No Gmail dot/plus-alias normalization (Opus SF-5): the server does none, and client-only
      // aliasing would create new false mismatches the server would never agree with.
      var sessionEmailNorm = String(sessionEmail).trim().toLowerCase();
      var tokenEmailNorm = String(claims.email).trim().toLowerCase();
      return sessionEmailNorm === tokenEmailNorm ? 'same_person' : 'mismatch';
    } catch (e) {
      // Never throw (round-2 Opus SF-4, test l) -- a thrown error here at parse time would kill
      // window.PWAuth sitewide for pw-favorites.js/pw-in-cart.js, strictly worse than any
      // mismatch bug this feature fixes. Fail to the value that NEVER clears anything.
      return 'indeterminate_undecodable';
    }
  }

  /*
   * Three-state kill switch (config/settings_schema.json `pw_identity_crosscheck`, stamped as
   * window.PW_IDENTITY_CROSSCHECK by layout/theme.liquid). Defaults CLOSED (fails to 'off', the
   * strict no-op state) if the global is missing/garbled for any reason -- never assume 'enforce'.
   */
  function identityCrosscheckMode() {
    var m = window.PW_IDENTITY_CROSSCHECK;
    return m === 'observe' || m === 'enforce' ? m : 'off';
  }

  /*
   * Telemetry (§2.6): one event, `library.identity_check`, PII-free ({decision, layer, mode} only
   * -- never an email or raw id). The `match` decision is the overwhelming majority case once
   * shipped at `observe` to 100% of logged-in traffic, so it is sampled to once per browser
   * session (round-2 Opus SF-4, test m) -- everything else (mismatch/same_person/indeterminate_*)
   * is rare and every occurrence matters, so those are NEVER sampled.
   */
  var MATCH_BEACON_SAMPLED_KEY = 'pw_identity_match_beaconed';

  function shouldSendIdentityBeacon(decision) {
    if (decision !== 'match') return true;
    try {
      if (sessionStorage.getItem(MATCH_BEACON_SAMPLED_KEY) === '1') return false;
      sessionStorage.setItem(MATCH_BEACON_SAMPLED_KEY, '1');
      return true;
    } catch (e) {
      return true; // storage blocked -- fail OPEN to sending rather than silently losing the signal
    }
  }

  function postIdentityCheckEvent(decision, layer, mode) {
    try {
      if (!shouldSendIdentityBeacon(decision)) return;
      var backendUrl = window.PW_BACKEND_URL;
      if (!backendUrl) return;
      var body = JSON.stringify({
        eventVersion: 1,
        event: 'library.identity_check',
        decision: decision,
        layer: layer,
        mode: mode,
      });
      var url = backendUrl + '/api/collect';
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else if (typeof fetch !== 'undefined') {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break the page */
    }
  }

  /*
   * Recovery backup (§2.5, Opus MF-3b). "trivially revertible by theme push" is false for this
   * change class: reverting a theme push stops FUTURE clears, it restores nothing already
   * deleted. So a mismatch-triggered clear backs up pw_favorites FIRST, under a single
   * (overwritten) slot, before signOutCleanup() removes it -- support can restore a false-positive
   * by copying this key back. Scope is deliberately narrow: ONLY the mismatch-reason clear backs
   * up. Ordinary signout/account_switch clears exist precisely to remove the PREVIOUS person's
   * data on a shared computer, and backing those up would reintroduce the exact leak they fix.
   */
  var FAVORITES_KEY = 'pw_favorites';
  var FAVORITES_BACKUP_KEY = 'pw_favorites_cleared_backup';
  var FAVORITES_BACKUP_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  function backupFavoritesBeforeMismatchClear(reason) {
    try {
      var data = localStorage.getItem(FAVORITES_KEY);
      if (data == null) return; // nothing to back up
      localStorage.setItem(
        FAVORITES_BACKUP_KEY,
        JSON.stringify({ ts: Date.now(), reason: reason, data: data }),
      );
    } catch (e) {
      /* best-effort -- a failed backup must never block the clear it exists to make safe */
    }
  }

  /* A later page load deletes the backup once it's stale (>30 days) -- called on every
   * syncIdentity() run, not just mismatch ones, so a customer who never triggers another mismatch
   * still has their earlier backup age out. */
  function pruneStaleFavoritesBackup() {
    try {
      var raw = localStorage.getItem(FAVORITES_BACKUP_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed.ts === 'number' && Date.now() - parsed.ts > FAVORITES_BACKUP_MAX_AGE_MS) {
        localStorage.removeItem(FAVORITES_BACKUP_KEY);
      }
    } catch (e) {
      /* malformed backup -- leave it rather than throw during a routine sync */
    }
  }

  /*
   * Layer 1 (§2.5) -- the sitewide guard. Runs only once PW_CUSTOMER_ID is known present (the
   * caller, syncIdentity(), already established that) and the kill switch isn't 'off'. A stored
   * token that doesn't exist yet is not this function's problem (nothing to check); a token that
   * exists but doesn't decode still gets a decision (indeterminate_undecodable) so the telemetry
   * signal stays visible (Opus SF-6) even though it never clears.
   */
  function runIdentityCrosscheck(sessionCustomerId) {
    var mode = identityCrosscheckMode();
    if (mode === 'off') return;

    var token;
    try {
      token = getToken();
    } catch (e) {
      return;
    }
    if (!token) return; // nothing stored to check

    var claims = tokenClaims(token);
    var sessionEmail = window.PW_CUSTOMER_EMAIL;
    var decision = identityCheck(claims, sessionCustomerId, sessionEmail);

    postIdentityCheckEvent(decision, 1, mode);

    if (mode === 'observe') return; // telemetry only -- zero storage writes/clears

    if (decision === 'mismatch') {
      backupFavoritesBeforeMismatchClear('token_identity_mismatch');
      signOutCleanup('token_identity_mismatch');
    }
    // match / same_person / indeterminate_* -- keep the token, nothing else to do.
  }

  function syncIdentity() {
    var current = window.PW_CUSTOMER_ID ? String(window.PW_CUSTOMER_ID) : '';
    var stamped;
    try {
      stamped = localStorage.getItem(CUSTOMER_STAMP_KEY) || '';
    } catch (e) {
      return; // can't read storage -- do nothing, never guess
    }

    // FIX-UP (2026-09-01, Opus S2 -- LOW-MEDIUM): pruneStaleFavoritesBackup() operates on a key
    // (pw_favorites_cleared_backup) that only exists because of #816 -- it must not run at `off`
    // either, or `off` is not genuinely byte-identical to pre-#816 behavior as promised.
    if (identityCrosscheckMode() !== 'off') {
      pruneStaleFavoritesBackup();
    }

    if (current) {
      if (stamped && stamped !== current) signOutCleanup('account_switch'); // switched accounts
      try {
        localStorage.setItem(CUSTOMER_STAMP_KEY, current);
      } catch (e) {
        /* storage full/blocked — stamp just won't persist this load */
      }
      // tasks.db #816 -- identity cross-check (Layer 1). Deliberately AFTER the existing
      // stamp/transition logic above, and deliberately still inside `if (current)`: the critical
      // invariant below (no PW_CUSTOMER_ID -> new code does not run at all) must stay true, so
      // this only ever runs once we already know a real session identity is present.
      runIdentityCrosscheck(window.PW_CUSTOMER_ID);
      return;
    }

    if (stamped) {
      // was signed in, now isn't -- a real sign-out.
      signOutCleanup('signout');
      try {
        localStorage.removeItem(CUSTOMER_STAMP_KEY);
      } catch (e) {
        /* blocked storage must not throw during sign-out */
      }
    }
    // no stamp AND no current customer: emailed-link visitor, never signed in. Do nothing.
  }

  syncIdentity();

  /*
   * bfcache (Opus SF-9): syncIdentity() above only runs once, at parse time -- a back/forward-
   * cache restore does NOT re-run parse-time script, so "every page load" was not literally true
   * for a bfcache-restored page. `pageshow` fires on every navigation INCLUDING a fresh load, but
   * `event.persisted` is only true for a bfcache restore, so this never double-fires the ordinary
   * case. syncIdentity() is idempotent by construction (a second run against unchanged storage
   * makes the same decision and takes the same, already-applied action), so a double run on a
   * normal load would be harmless even if this guard were ever loosened.
   */
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', function (e) {
      // FIX-UP (2026-09-01, Opus S2 -- LOW-MEDIUM): this ENTIRE listener is new #816 code (Opus
      // SF-9) -- before #816, syncIdentity() ran exactly once, at parse time, full stop. A
      // bfcache restore at `off` must not gain a second syncIdentity() run it never had before:
      // that run could reach a genuinely different decision than the original parse-time run if
      // another tab changed pw_customer_id_stamp in the interim (a cross-tab account-switch +
      // bfcache restore wiping the wrong tab's session) -- a NEW failure mode `off` didn't
      // previously have, contradicting the promise that `off` is byte-identical to pre-#816.
      if (identityCrosscheckMode() === 'off') return;
      if (e && e.persisted) syncIdentity();
    });
  }

  window.PWAuth = {
    TOKEN_KEY: TOKEN_KEY,
    getToken: getToken,
    storeToken: storeToken,
    clearToken: clearToken,
    tokenExp: tokenExp,
    tokenClaims: tokenClaims,
    shouldReplaceToken: shouldReplaceToken,
    identityCheck: identityCheck,
    /* Exported (2026-09-01 fix-up, Sonnet #4) so pw-library.js can call this instead of
     * maintaining its own verbatim-duplicate local copy -- matching how it already reuses
     * identityCheck/tokenClaims/postIdentityCheckEvent rather than a second implementation of
     * any of those. */
    identityCrosscheckMode: identityCrosscheckMode,
    /* Exported (not just used internally by Layer 1/runIdentityCrosscheck above) so pw-library.js's
     * Layer 2 render gate emits the SAME `library.identity_check` event via the SAME sampling
     * state -- the `match` sampling flag (§2.6, test m) is a once-PER-SESSION budget shared across
     * both layers, not once-per-layer; two independent beacon implementations would double-count
     * (or, worse, drift on the sampling rule itself). */
    postIdentityCheckEvent: postIdentityCheckEvent,
  };
})();
